<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Switch } from "@/components/ui/switch";
  import CreativeLivePreview from "@/components/MCampaign/Creatives/CreativeLivePreview.vue";
  import type { AdTemplateHtml, AdTemplatePlacement, AdTemplateVariables } from "@/types";

  const props = defineProps<{
    documentUrl: string;
    htmlTemplates: AdTemplateHtml;
    variables: AdTemplateVariables;
    backgrounds: string[];
    refreshDocumentUrl: () => Promise<void>;
  }>();

  const placement = ref<AdTemplatePlacement>("feed");
  const texts = ref<Record<string, string>>({});
  const background = ref<string>("");

  const resetTexts = () => {
    texts.value = Object.fromEntries(Object.keys(props.variables).map((key) => [key, ""]));
  };

  watch(
    () => Object.keys(props.variables).join("|"),
    () => resetTexts(),
    { immediate: true },
  );

  watch(
    () => props.backgrounds,
    (urls) => {
      if (!background.value && urls.length) background.value = urls[0] ?? "";
    },
    { immediate: true },
  );

  const template = computed(() => props.htmlTemplates[placement.value]);
  const previewVariables = computed(() => {
    const resolved = Object.fromEntries(
      Object.entries(props.variables).map(([key, spec]) => {
        const value = String(texts.value[key] ?? "");
        return [key, value.trim() ? value : String(spec.default ?? "")];
      }),
    );
    return { ...resolved, background_image: background.value };
  });
</script>

<template>
  <div class="flex flex-col gap-4">
    <label class="flex items-center gap-2">
      <span class="text-sm font-medium">Feed</span>
      <Switch
        :model-value="placement === 'story'"
        @update:model-value="placement = $event ? 'story' : 'feed'" />
      <span class="text-sm font-medium">Story</span>
    </label>

    <CreativeLivePreview
      :document-url="documentUrl"
      :placement="placement"
      :variables="previewVariables"
      :template="template"
      :refresh-document-url="refreshDocumentUrl" />

    <div class="flex flex-col gap-2">
      <Label class="heading-label">Test texts</Label>
      <div
        v-for="(spec, key) in variables"
        :key="key"
        class="flex items-center gap-2">
        <span class="w-20 shrink-0 truncate text-xs text-muted-foreground">{{ spec.label || key }}</span>
        <Input
          v-model="texts[key]"
          class="text-sm"
          :placeholder="spec.default" />
      </div>
    </div>

    <div
      v-if="backgrounds.length"
      class="flex flex-col gap-2">
      <Label class="heading-label">Test background</Label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="url in backgrounds"
          :key="url"
          type="button"
          class="size-12 overflow-hidden rounded-md border-2"
          :class="url === background ? 'border-primary' : 'border-border'"
          @click="background = url">
          <img
            :src="url"
            alt="Test background"
            class="size-full object-cover" />
        </button>
      </div>
    </div>
  </div>
</template>
